import { useRef, useState, useEffect } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useWorkAssignments } from '../../hooks/workAssignmentHooks';
import { StylishNewSearchBar } from 'components/DesignSystems/New/StylishNewSearchBar';
import { WorkAssignmentItem } from './WorkAnalysisItems/WorkAssignmentItem';
import { AddWorkAssignmentCard } from './WorkAnalysisItems/AddWorkAssignmentCard';
import { ScrollableList } from 'components/DesignSystems/ScrollableList';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { useResourcesInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import { ResourceTotalModal } from './WorkAnalysisItems/ResourceTotalModal';
import { incidentCardOperationalPlanningWorksheetTitle } from '../USCGPage';

type WorkAssignmentProps = {
  selectedTactics: string[];
  setPoint2: (point: { x: number; y: number } | null) => void;
  setSelectedTactics: (tactics: string[]) => void;
  readOnly?: boolean;
};

const WorkAssignment = ({
  selectedTactics,
  setPoint2,
  setSelectedTactics,
  readOnly = false,
}: WorkAssignmentProps) => {
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResourceTotalModal, setShowResourceTotalModal] = useState(false);

  const { data: roster } = useGetPositionsForIncident();
  const { data: resources } = useResourcesInIncident();

  const { data: workAssignments } = useWorkAssignments();

  const tacticNameRef = useRef<HTMLDivElement | null>(null);
  const observeRef = useRef<HTMLDivElement | null>(null);
  const { exportAsForm } = useIcsFormPdfPreview();

  const handleExportICS215 = () => {
    void exportAsForm({ formname: 'ICS_215_CG', data: {}, mapSketch: null });
  };
  const updatePoint2 = () => {
    if (selectedTactics && tacticNameRef.current) {
      const rect = tacticNameRef.current.getBoundingClientRect();
      setPoint2({
        x: rect.left - 5,
        y: rect.top + rect.height / 4,
      });
    }
  };

  const resourceTotal: {
    [resourceName: string]: { have: number; need: number; required: number };
  } = {};

  workAssignments?.forEach((assignment) => {
    assignment.resource_list?.forEach((resource: any) => {
      const name = resource.resourceName;
      const have = parseInt(resource.have, 10) || 0;
      const need = parseInt(resource.need, 10) || 0;
      const required = parseInt(resource.required, 10) || 0;

      if (resourceTotal[name]) {
        resourceTotal[name].have += have;
        resourceTotal[name].need += need;
        resourceTotal[name].required += required;
      } else {
        resourceTotal[name] = { have, need, required };
      }
    });
  });

  useEffect(() => {
    if (!tacticNameRef.current) return;
    let lastX: number | null = null;
    let lastY: number | null = null;
    const interval = setInterval(() => {
      const rect = tacticNameRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (rect.left !== lastX || rect.top !== lastY) {
        lastX = rect.left;
        lastY = rect.top;
        updatePoint2();
      }
    }, 20);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTactics, setPoint2]);

  const filteredWorkAssignments = workAssignments?.filter(
    (work) =>
      !work.archived &&
      (selectedTactics?.length === 0 ||
        work.parent_tactics?.some((id) => selectedTactics?.includes(id))) &&
      work.work_assignment.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const workGroups =
    roster
      ?.filter((member) => member.is_workgroup)
      .map((wg) => ({ id: wg.id, name: wg.name })) || [];

  const resourceOptions = [
    ...Object.keys(resourceTotal).map((key) => ({
      label: key,
      value: key,
    })),
    ...(resources?.map((res) => ({
      label: res.resource_name || '',
      value: res.id,
    })) || []),
  ];

  return (
    <div className="w-100">
      <div ref={observeRef} className="work-card-header">
        <div ref={tacticNameRef}>
          {incidentCardOperationalPlanningWorksheetTitle}
        </div>
        <div>Tactics: {selectedTactics?.length}</div>
        <StylishNewButton
          className="button--link ms-2"
          onClick={() => {
            setSelectedTactics([]);
          }}
          disabled={selectedTactics?.length === 0}
        >
          <SharedIcon iconName="clear" />
        </StylishNewButton>
        <div className="d-flex gap-2">
          <StylishNewSearchBar
            onChangeSearchTerm={(term) => setSearchTerm(term)}
          />
          {!readOnly && (
            <StylishNewButton
              onClick={() => {
                setAdding(!adding);
              }}
              disabled={selectedTactics?.length > 0 ? false : true}
            >
              <SharedIcon iconName="add" classes="me-2" />
              Add Work Assignment
            </StylishNewButton>
          )}
          <StylishNewButton
            onClick={handleExportICS215}
            className="button--primary"
          >
            Preview ICS-215
          </StylishNewButton>
        </div>
      </div>
      <StylishNewButton
        onClick={() => setShowResourceTotalModal(true)}
        className="button--secondary mb-2"
      >
        Show Resource totals
      </StylishNewButton>
      {adding && !readOnly && (
        <AddWorkAssignmentCard
          setAdding={setAdding}
          workGroups={workGroups}
          resourceOptions={resourceOptions}
          selectedTactics={selectedTactics}
        />
      )}
      <ScrollableList hideScrollbar viewportHeightPercentage={80}>
        {filteredWorkAssignments?.map((work, i) => (
          <div key={i} className="mb-2">
            <WorkAssignmentItem
              work={work}
              workGroups={workGroups}
              resourceOptions={resourceOptions}
              readOnly={readOnly}
            />
          </div>
        ))}
      </ScrollableList>
      <div className="work-card-footer">
        {filteredWorkAssignments?.length} Work Assignments
      </div>
      {showResourceTotalModal && (
        <ResourceTotalModal
          resourceTotal={resourceTotal}
          showModal={showResourceTotalModal}
          setShowModal={setShowResourceTotalModal}
        />
      )}
    </div>
  );
};

export default WorkAssignment;
